// ============================================================
// TOPBAR — ハンバーガー（md:hidden / aria-label でドロワーを開く）+
// 統合検索（Enter / 虫眼鏡で /search?q=... へ遷移＝SearchView を起動）+
// 通知ベル（期限超過＋今日が期限の自分のタスク）+
// DEV: 閲覧ユーザー切替（dev viewer-switch）/ LIVE: ログアウト + アバター。
//
// 通知ベルの件数定義（お知らせ機能は 2026-07-10 撤去＝Slack に一本化）：
//   • 期限超過タスク数 = myTasks（自分担当）のうち 未完了 && due < TODAY。
//   • 今日が期限のタスク数 = myTasks（自分担当）のうち 未完了 && due === TODAY。
// バッジ = 両者の合計（0件なら非表示）。パネルは2セクション・クリックで /tasks。
// タスクを完了する（subscribeTasks で即反映）ことで自然に消える。
// ============================================================
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useViewer } from '../auth/AuthContext';
import { getAdapter } from '../data/adapter';
import { Icon } from './Icons';
import { myTasks } from '../lib/permissions';
import { TODAY } from '../data/constants';
import type { Task } from '../types/models';

/** ドロップダウンの各セクションで一覧表示する最大件数（超過分は「他N件」）。 */
const PANEL_LIMIT = 5;

export default function TopBar({
  onOpenDrawer,
  onOpenGuide,
}: {
  onOpenDrawer: () => void;
  /** 「ガイド」導線 — 初回オンボーディングをいつでも再表示する。 */
  onOpenGuide: () => void;
}) {
  const { setViewer, switchable, mode, signOut } = useAuth();
  const viewer = useViewer();
  const navigate = useNavigate();
  const [q, setQ] = useState('');

  // 統合検索を起動（query を /search?q= に渡す。空なら無視）。
  const runSearch = () => {
    const t = q.trim();
    if (t) navigate(`/search?q=${encodeURIComponent(t)}`);
  };

  // ── 通知（期限超過＋今日が期限の自分のタスク）──
  const [tasks, setTasks] = useState<Task[] | null>(null);
  const [open, setOpen] = useState(false);
  const popRef = useRef<HTMLDivElement | null>(null);

  // tasks を取得（getAdapter 経由＝live/dev 両対応）。失敗しても落とさない。
  // viewer 切替（DEV）でも live の権限スコープと整合するよう viewer.id で再取得する。
  // tasks は初期 get（下の購読が確立できない場合の保険）＋ subscribeTasks が正
  // （Home.tsx と同じ流儀）＝タスクを完了すればバッジが即座に消える。
  useEffect(() => {
    let alive = true;
    setTasks(null); // viewer 切替時は前の閲覧者のデータを残さない
    getAdapter()
      .then((a) => a.getTasks())
      .then((t) => {
        if (!alive) return;
        // 購読の初回 emit が先に届いていたら古い get 結果で上書きしない（Home.tsx と同じ）。
        setTasks((prev) => prev ?? t);
      })
      .catch(() => {
        if (!alive) return;
        setTasks((prev) => prev ?? []); // 取得失敗時は空配列＝通知が出ないだけ（安全側）
      });
    return () => {
      alive = false;
    };
  }, [viewer.id]);

  // tasks をリアルタイム購読（unmount / viewer 切替で必ず購読解除）。購読エラーは
  // adapter 側で console.warn 握りつぶし＝既存の get 結果で動き続ける。
  // mock は即時1回だけ流れる（DEV挙動は実質不変）。
  useEffect(() => {
    let alive = true;
    let unsub: (() => void) | null = null;
    void getAdapter().then((a) => {
      if (!alive) return;
      unsub = a.subscribeTasks((t) => setTasks(t));
    });
    return () => {
      alive = false;
      if (unsub) unsub(); // unmount で必ず購読解除
    };
  }, [viewer.id]);

  // viewer 切替時はパネルを閉じる（他人の通知を開いたままにしない）。
  useEffect(() => {
    setOpen(false);
  }, [viewer.id]);

  // 期限超過タスク：自分担当×未完了×期限が今日より前。期限の古い順（超過が深い順）。
  const overdueTasks = useMemo(() => {
    if (!tasks) return [] as Task[];
    return myTasks(viewer, tasks)
      .filter((t) => t.status !== 'done' && !!t.due && t.due < TODAY)
      .slice()
      .sort((a, b) => a.due.localeCompare(b.due));
  }, [tasks, viewer]);

  // 今日が期限のタスク：自分担当×未完了×期限が今日。
  const dueTodayTasks = useMemo(() => {
    if (!tasks) return [] as Task[];
    return myTasks(viewer, tasks).filter((t) => t.status !== 'done' && t.due === TODAY);
  }, [tasks, viewer]);

  // バッジ = 期限超過 + 今日が期限。0件なら非表示。
  const badge = overdueTasks.length + dueTodayTasks.length;

  // パネル開閉。
  const toggleOpen = () => {
    setOpen((prev) => !prev);
  };

  // 外側クリック / Esc で閉じる。
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const go = (to: string) => {
    setOpen(false);
    navigate(to);
  };

  return (
    <header className="material-chrome scroll-edge sticky top-0 z-30 h-16">
      <div className="h-full px-4 sm:px-6 lg:px-8 flex items-center gap-3">
        {/* hamburger — mobile only */}
        <button
          type="button"
          onClick={onOpenDrawer}
          aria-label="メニューを開く"
          className="md:hidden rounded-soft p-2 text-slate-600 hover:bg-base"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M3 6h18M3 12h18M3 18h18" />
          </svg>
        </button>

        {/* 統合検索 — Enter / 虫眼鏡クリックで /search?q= へ遷移 */}
        <div className="flex-1 min-w-0 max-w-md">
          <div className="relative block">
            <button
              type="button"
              onClick={runSearch}
              aria-label="検索"
              className="absolute left-1.5 top-1/2 -translate-y-1/2 rounded-soft p-1.5 text-slate-400 hover:text-charcoal hover:bg-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-brand/40"
            >
              <svg
                width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="m21 21-4.3-4.3" />
              </svg>
            </button>
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') runSearch();
              }}
              aria-label="議事録・タスク・ナレッジ・予定を横断検索"
              placeholder="議事録・タスク・ナレッジを横断検索"
              className="w-full rounded-full border border-transparent bg-charcoal/[0.06] pl-10 pr-4 py-2 text-sm placeholder:text-slate-400 transition-colors focus:bg-white focus:border-hair focus:shadow-sm focus-visible:outline-none"
            />
          </div>
        </div>

        <div className="ml-auto flex items-center gap-2 sm:gap-3">
          {/* ガイド — 初回オンボーディングをいつでも再表示 */}
          <button
            type="button"
            onClick={onOpenGuide}
            aria-label="はじめてガイドを開く"
            title="はじめてガイド"
            className="inline-flex items-center gap-1.5 rounded-soft px-2 py-2 text-slate-600 hover:bg-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-brand/40"
          >
            <Icon name="life-buoy" size={20} />
            <span className="hidden sm:inline text-sm">ガイド</span>
          </button>

          {/* notifications — 期限超過＋今日が期限の自分のタスク */}
          <div className="relative" ref={popRef}>
            <button
              type="button"
              onClick={toggleOpen}
              aria-label={badge > 0 ? `通知 ${badge} 件` : '通知'}
              aria-haspopup="true"
              aria-expanded={open}
              className="relative rounded-soft p-2 text-slate-600 hover:bg-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-brand/40"
            >
              <Icon name="bell" size={20} />
              {badge > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 grid place-items-center rounded-full bg-amber-brand text-white text-2xs font-bold font-num ring-2 ring-white">
                  {badge > 9 ? '9+' : badge}
                </span>
              )}
            </button>

            {open && (
              <div
                className="material-panel pop-in absolute right-0 mt-2 w-[88vw] max-w-sm rounded-card overflow-hidden z-40"
                role="menu"
                aria-label="通知"
              >
                <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-hair">
                  <div className="text-sm font-bold text-charcoal">通知</div>
                  {badge > 0 && (
                    <span className="text-2xs font-medium text-amber-deep bg-amber-soft border border-amber-brand/30 rounded-full px-2 py-0.5">
                      {badge} 件
                    </span>
                  )}
                </div>

                {badge === 0 ? (
                  <div className="px-4 py-10 text-center text-xs text-slate-sub">新しい通知はありません</div>
                ) : (
                  <div className="max-h-[60vh] overflow-y-auto">
                    {/* ── 期限超過のタスク（クリックで /tasks へ）── */}
                    {overdueTasks.length > 0 && (
                      <div className="border-b border-hair last:border-b-0">
                        <p className="px-4 pt-3 pb-1 text-2xs font-bold text-danger-deep">
                          期限超過のタスク <span className="font-num">{overdueTasks.length}</span>
                        </p>
                        <ul>
                          {overdueTasks.slice(0, PANEL_LIMIT).map((t) => (
                            <li key={t.id}>
                              <button
                                type="button"
                                onClick={() => go('/tasks')}
                                role="menuitem"
                                className="w-full text-left px-4 py-2.5 flex items-start gap-3 hover:bg-base/50"
                              >
                                <span className="mt-0.5 grid place-items-center w-7 h-7 rounded-full shrink-0 bg-danger-deep/10 text-danger-deep">
                                  <Icon name="list-todo" size={15} />
                                </span>
                                <span className="min-w-0 flex-1">
                                  <span className="block text-sm text-charcoal truncate">{t.title}</span>
                                  <span className="mt-0.5 flex items-center gap-2 text-2xs">
                                    <span className="font-num text-danger-deep font-semibold">期限 {t.due}</span>
                                  </span>
                                </span>
                              </button>
                            </li>
                          ))}
                        </ul>
                        {overdueTasks.length > PANEL_LIMIT && (
                          <p className="px-4 pb-2 text-2xs text-slate-400">他 {overdueTasks.length - PANEL_LIMIT} 件</p>
                        )}
                      </div>
                    )}

                    {/* ── 今日が期限のタスク（クリックで /tasks へ）── */}
                    {dueTodayTasks.length > 0 && (
                      <div>
                        <p className="px-4 pt-3 pb-1 text-2xs font-bold text-amber-deep">
                          今日が期限のタスク <span className="font-num">{dueTodayTasks.length}</span>
                        </p>
                        <ul>
                          {dueTodayTasks.slice(0, PANEL_LIMIT).map((t) => (
                            <li key={t.id}>
                              <button
                                type="button"
                                onClick={() => go('/tasks')}
                                role="menuitem"
                                className="w-full text-left px-4 py-2.5 flex items-start gap-3 hover:bg-base/50"
                              >
                                <span className="mt-0.5 grid place-items-center w-7 h-7 rounded-full shrink-0 bg-amber-soft text-amber-deep">
                                  <Icon name="list-todo" size={15} />
                                </span>
                                <span className="min-w-0 flex-1">
                                  <span className="block text-sm text-charcoal truncate">{t.title}</span>
                                  <span className="mt-0.5 flex items-center gap-2 text-2xs">
                                    <span className="font-num text-amber-deep font-semibold">期限 今日（{t.due}）</span>
                                  </span>
                                </span>
                              </button>
                            </li>
                          ))}
                        </ul>
                        {dueTodayTasks.length > PANEL_LIMIT && (
                          <p className="px-4 pb-2 text-2xs text-slate-400">他 {dueTodayTasks.length - PANEL_LIMIT} 件</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* DEV: 閲覧ユーザー切替 / LIVE: ログアウト */}
          {mode === 'dev' ? (
            <label className="hidden sm:flex items-center gap-1.5">
              <span className="text-2xs text-slate-sub">表示</span>
              <select
                value={viewer.id}
                onChange={(e) => setViewer(e.target.value)}
                aria-label="閲覧ユーザーを切り替え"
                className="rounded-soft border border-hair bg-white px-2 py-1.5 text-sm text-charcoal focus:border-sky-brand"
              >
                {switchable.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <button
              type="button"
              onClick={() => void signOut()}
              className="hidden sm:inline-flex items-center rounded-soft border border-hair bg-white px-3 py-1.5 text-sm text-charcoal hover:bg-base"
            >
              ログアウト
            </button>
          )}

          {/* avatar */}
          <span
            className="grid place-items-center w-9 h-9 rounded-full bg-charcoal text-white text-sm font-bold"
            title={viewer.name}
          >
            {viewer.initials}
          </span>
        </div>
      </div>
    </header>
  );
}
